"use client";

import { useBuilder } from "@/context/BuilderContext";
import { formatPrice } from "@/lib/utils";
import { cn } from "@/lib/utils";
import { Check, Ruler, Send } from "lucide-react";
import { useTranslations } from "next-intl";
import { useBuilderOptions } from "@/hooks/useBuilderOptions";

export function StepCapSize() {
  const { selections, setOption } = useBuilder();
  const selected = selections.cap_size;
  const t = useTranslations("builder");
  const { options } = useBuilderOptions();
  const dbPrices = new Map((options.cap_size || []).map((o) => [o.value, o.price]));

  const CAP_SIZES = [
    {
      value: "Petite",
      label: t("petiteName"),
      measurement: t("petiteMeasurement"),
      price: dbPrices.get("Petite") ?? 0,
      description: t("petiteDesc"),
    },
    {
      value: "Average",
      label: t("averageName"),
      measurement: t("averageMeasurement"),
      price: dbPrices.get("Average") ?? 0,
      description: t("averageDesc"),
      popular: true,
    },
    {
      value: "Large",
      label: t("largeName"),
      measurement: t("largeMeasurement"),
      price: dbPrices.get("Large") ?? 0,
      description: t("largeDesc"),
    },
    {
      value: "Custom Fit",
      label: t("customFitName"),
      measurement: t("customFitMeasurement"),
      price: dbPrices.get("Custom Fit") ?? 300,
      description: t("customFitDesc"),
      isCustom: true,
    },
  ];

  return (
    <div className="pt-2">
      {/* Measurement guide */}
      <div className="mb-6 p-4 bg-brand-cream rounded-lg border border-theme-border/60">
        <div className="flex items-start gap-3">
          <Ruler className="w-5 h-5 text-brand-gold flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-medium text-foreground">
              {t("measureGuideTitle")}
            </p>
            <p className="text-xs text-brand-muted mt-1 leading-relaxed">
              {t("capSizeGuideHow")}
            </p>
          </div>
        </div>
      </div>

      {/* Options */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        {CAP_SIZES.map((option) => {
          const isSelected = selected === option.value;

          return (
            <button
              key={option.value}
              onClick={() => setOption("cap_size", option.value, option.price)}
              className={cn(
                "relative text-start rounded-lg border-2 transition-all duration-300 group active:scale-[0.98]",
                "hover:shadow-md hover:border-brand-gold-light",
                // Bug #113: the Popular badge + title were both 16px
                // from the top of the card, so on mobile (p-4) the
                // badge landed directly on the "Average" heading.
                // Adding extra top padding when a popular badge shows
                // leaves breathing room; non-popular cards keep the
                // compact layout.
                option.popular ? "pt-11 px-4 pb-4 sm:pt-12 sm:px-6 sm:pb-6" : "p-4 sm:p-6",
                isSelected
                  ? "border-brand-gold bg-brand-gold/[0.03] shadow-md"
                  : "border-theme-border bg-surface"
              )}
            >
              {/* Selected checkmark */}
              {isSelected && (
                <div className="absolute top-4 end-4 w-6 h-6 bg-brand-gold rounded-full flex items-center justify-center animate-scale-in">
                  <Check className="w-3.5 h-3.5 text-white" />
                </div>
              )}

              {/* Popular badge */}
              {option.popular && (
                <span className="absolute top-4 start-4 text-[10px] tracking-wider uppercase font-medium text-brand-gold bg-brand-gold/10 px-2 py-0.5 rounded-full">
                  {t("popular")}
                </span>
              )}

              {/* Icon for custom */}
              {option.isCustom && (
                <div className="w-10 h-10 rounded-lg bg-brand-gold/10 flex items-center justify-center mb-3">
                  <Send className="w-4 h-4 text-brand-gold" />
                </div>
              )}

              {/* Size name + measurement */}
              <h3 className="font-display text-lg text-foreground mb-0.5">
                {option.label}
              </h3>
              <p
                className={cn(
                  "text-sm font-medium mb-2",
                  isSelected ? "text-brand-gold" : "text-brand-muted"
                )}
              >
                {option.measurement}
              </p>
              <p className="text-xs text-brand-muted leading-relaxed">
                {option.description}
              </p>

              {/* Price */}
              <div className="mt-4 pt-3 border-t border-theme-border/60">
                <span
                  className={cn(
                    "text-sm font-medium",
                    option.price === 0 ? "text-green-600" : "text-foreground"
                  )}
                >
                  {option.price === 0
                    ? t("included")
                    : `+${formatPrice(option.price)}`}
                </span>
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}
